import { useState, useCallback, useEffect } from 'react'
import { message } from 'antd'
import {
  getPresignedUrls,
  generateNewStyle,
  listEditingStyles,
  deleteEditingStyles
} from '../../../services/api/aiVideoGeneration'
import useTaskPoller from './useTaskPoller'

/**
 * Custom hook to manage shot generation styles
 * Handles fetching presigned URLs, uploading videos, and creating new styles
 * Now supports polling for task completion
 */
export const useShotGenerationStyles = ({ videoId } = {}) => {
  const [customStyles, setCustomStyles] = useState([])
  const [systemStyles, setSystemStyles] = useState([])
  const [isLoadingStyles, setIsLoadingStyles] = useState(false)

  const [isCreatingStyle, setIsCreatingStyle] = useState(false)
  const [isDeletingStyle, setIsDeletingStyle] = useState(false)
  const [deletingStyleId, setDeletingStyleId] = useState(null)
  const [uploadProgress, setUploadProgress] = useState({})
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [currentAction, setCurrentAction] = useState('') // 'generating_urls', 'uploading', 'creating_style'

  // Initialize pollers for different tasks
  const presignedUrlPoller = useTaskPoller({ pollInterval: 2000 })
  const styleCreationPoller = useTaskPoller({ pollInterval: 3000 })
  const deletePoller = useTaskPoller({ pollInterval: 2000 })

  // All available styles (system + custom)
  const allStyles = [...systemStyles, ...customStyles]

  // Fetch styles on mount
  useEffect(() => {
    fetchStyles()
  }, [videoId])

  const fetchStyles = async () => {
    try {
      setIsLoadingStyles(true)
      const response = await listEditingStyles({ video_id: videoId })

      const resData = response.data

      // Case 1: Direct success
      if (resData && resData.status === 'success' && resData.output) {
        return resData.output
      }

      // Case 2: Async task, need to poll
      const taskId =
        resData.task_id ||
        resData.output?.task_id ||
        (resData.status === 'pending' && resData.id)

      let data = ''
      if (taskId) {
        console.log(`Polling for presigned URLs (Task ID: ${taskId})...`)
        data = await presignedUrlPoller.startPolling(taskId)
      }

      if (data && data.output) {
        // Map API response to our internal style format
        const responseOutput = data.output || {}
        const rawSystemStyles = responseOutput.system_styles || []
        const rawUserStyles = responseOutput.user_styles || []

        // Helper function for consistent mapping
        const mapStyleItem = (item, isSystemDefault = false) => {
          return {
            key: item.id || item.style_name, // Use ID if available, generic key fallback
            title: item.style_name,
            description: item.style_description,
            iconType: 'experiment', // Default icon
            // Use item.is_system if explicitly provided, otherwise fallback to the group default
            isSystem:
              item.is_system !== undefined ? !!item.is_system : isSystemDefault,
            isDefault: !!item.is_default,
            thumbnailVideo: item.style_thumbnail_video,
            thumbnailImage: item.style_thumbnail_img,
            raw: item
          }
        }

        const mappedSystem = rawSystemStyles
          .map(item => mapStyleItem(item, true))
          .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
        const mappedUser = rawUserStyles.map(item => mapStyleItem(item, false))

        // Prepend default style if it's not already in the list
        // const finalSystemStyles = [DEFAULT_SHOT_STYLE, ...mappedSystem]

        setSystemStyles(mappedSystem)
        setCustomStyles(mappedUser)
      }
    } catch (error) {
      console.error('Failed to list editing styles:', error)
      // Fallback or silent fail?
    } finally {
      setIsLoadingStyles(false)
    }
  }

  /**
   * Upload a single file to presigned URL
   */
  const uploadFileToPresignedUrl = useCallback(
    async (file, presignedUrl, fileId, contentType = 'video/mp4') => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            )
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: percentComplete
            }))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true })
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'))
        })

        xhr.open('PUT', presignedUrl)
        xhr.setRequestHeader('Content-Type', contentType)
        xhr.send(file)
      })
    },
    []
  )

  /**
   * Get presigned URLs for video uploads with polling support
   */
  const fetchPresignedUrls = useCallback(
    async (videoCount, includeThumbnailImage = true, isSystemStyle = false) => {
      try {
        setCurrentAction('generating_urls')
        const response = await getPresignedUrls({
          video_count: videoCount,
          include_thumbnail_image: includeThumbnailImage,
          is_system: isSystemStyle,
          video_id: videoId
        })

        const data = response.data

        // Case 1: Direct success
        if (data && data.status === 'success' && data.output) {
          return data.output
        }

        // Case 2: Async task, need to poll
        const taskId =
          data.task_id ||
          data.output?.task_id ||
          (data.status === 'pending' && data.id)

        if (taskId) {
          console.log(`Polling for presigned URLs (Task ID: ${taskId})...`)
          const result = await presignedUrlPoller.startPolling(taskId)
          return result.output
        }

        throw new Error('Failed to get presigned URLs: Validation error')
      } catch (error) {
        console.error('Error fetching presigned URLs:', error)
        throw error
      }
    },
    [presignedUrlPoller, videoId]
  )

  /**
   * Upload videos and create a new custom style
   */
  const createCustomStyle = useCallback(
    async ({
      styleName,
      styleDescription,
      videoFiles,
      thumbnailVideoFile,
      thumbnailImageFile,
      isSystemStyle = false
    }) => {
      if (!styleName || !videoFiles || videoFiles.length === 0) {
        message.error('Please provide style name and at least one video')
        return { success: false }
      }

      setIsCreatingStyle(true)
      setUploadProgress({})
      setCurrentAction('starting')

      try {
        // Step 1: Get presigned URLs (now with polling)
        const presignedData = await fetchPresignedUrls(
          videoFiles.length,
          !!thumbnailImageFile,
          isSystemStyle
        )

        const { upload_urls, thumbnail_image, thumbnail_video } = presignedData

        if (!upload_urls) {
          throw new Error('No upload URLs returned')
        }

        // Step 2: Upload training videos
        setCurrentAction('uploading')
        const videoUrls = []
        for (let i = 0; i < videoFiles.length; i++) {
          const file = videoFiles[i]
          const uploadInfo = upload_urls[i]

          await uploadFileToPresignedUrl(
            file,
            uploadInfo.upload_url,
            file.uid || uploadInfo.video_id,
            file.type || 'video/mp4'
          )

          videoUrls.push(uploadInfo.video_url)
        }

        // Step 3: Upload thumbnail video if provided
        let thumbnailVideoUrl = null
        if (thumbnailVideoFile && thumbnail_video) {
          await uploadFileToPresignedUrl(
            thumbnailVideoFile,
            thumbnail_video.upload_url,
            'thumbnail_video',
            thumbnailVideoFile.type || 'video/mp4'
          )
          thumbnailVideoUrl = thumbnail_video.video_url
        }

        // Step 4: Upload thumbnail image if provided
        let thumbnailImageUrl = null
        if (thumbnailImageFile && thumbnail_image) {
          // Backend generates .jpg filenames and signs for image/jpeg
          // We must ensure the Content-Type header matches the signature exactly to avoid 403s
          const imageType = 'image/jpeg'
          await uploadFileToPresignedUrl(
            thumbnailImageFile,
            thumbnail_image.upload_url,
            'thumbnail_image',
            imageType
          )
          thumbnailImageUrl = thumbnail_image.image_url
        }

        // Step 5: Create the new style (now with polling)
        setCurrentAction('creating_style')
        const stylePayload = {
          style_name: styleName,
          style_description: styleDescription || '',
          is_system: isSystemStyle,
          video_urls: videoUrls,
          style_thumbnail_video: thumbnailVideoUrl || videoUrls[0], // Use first video as fallback
          style_thumbnail_img: thumbnailImageUrl,
          video_id: videoId
        }

        const response = await generateNewStyle(stylePayload)
        const data = response.data

        let newStyleData = null

        // Support both direct response and polling
        const taskId = data.task_id || (data.status === 'pending' && data.id)

        if (taskId) {
          console.log(`Polling for style creation (Task ID: ${taskId})...`)
          const pollResult = await styleCreationPoller.startPolling(taskId)
          newStyleData = pollResult.output || pollResult // Depending on API consistency

          // If polling returns wrapper {output: {...}}, unwrap it
          if (newStyleData.output) {
            newStyleData = newStyleData.output
          }
        } else if (data.status === 'success' || data.success) {
          newStyleData = data.output || data
        } else {
          throw new Error('Failed to initiate style creation')
        }
        if (newStyleData) {
          // Identify thumbnail from new style data output
          let finalThumbnailVideo = newStyleData.style_thumbnail_video
          if (
            !finalThumbnailVideo &&
            newStyleData.style_video &&
            newStyleData.style_video.length > 0
          ) {
            finalThumbnailVideo = newStyleData.style_video[0].video_link
          }

          // Add new style to custom styles list
          const newStyle = {
            key:
              newStyleData.id || styleName.toLowerCase().replace(/\s+/g, '_'),
            title: newStyleData.style_name || styleName,
            description: newStyleData.style_description || styleDescription,
            iconType: 'experiment',
            isSystem: newStyleData.is_system || isSystemStyle,
            thumbnailVideo: finalThumbnailVideo,
            thumbnailImage: newStyleData.style_thumbnail
          }

          // Refresh library to include the new style
          fetchStyles()

          if (newStyle.isSystem) {
            setSystemStyles(prev => [...prev, newStyle])
            message.success('System style created successfully!')
          } else {
            setCustomStyles(prev => [...prev, newStyle])
            message.success('Custom style created successfully!')
          }
          setIsModalVisible(false)

          return { success: true, style: newStyle }
        }

        throw new Error('Failed to create style')
      } catch (error) {
        console.error('Error creating custom style:', error)
        message.error('Failed to create custom style. Please try again.')
        return { success: false, error }
      } finally {
        setIsCreatingStyle(false)
        setCurrentAction('')
      }
    },
    [
      fetchPresignedUrls,
      uploadFileToPresignedUrl,
      styleCreationPoller,
      fetchStyles,
      videoId
    ]
  )

  /**
   * Delete an existing style
   */
  const deleteStyle = useCallback(
    async (styleId, isSystem = false) => {
      try {
        setIsDeletingStyle(true)
        setDeletingStyleId(styleId)
        const response = await deleteEditingStyles({
          style_id: styleId,
          video_id: videoId
        })

        const resData = response.data

        // Case 1: Direct success
        if (resData?.status === 'success' || resData?.success) {
          if (isSystem) {
            setSystemStyles(prev => prev.filter(style => style.key !== styleId))
          } else {
            setCustomStyles(prev => prev.filter(style => style.key !== styleId))
          }
          message.success('Style deleted successfully')
          return { success: true }
        }

        // Case 2: Async task, need to poll
        const taskId =
          resData?.task_id ||
          resData?.output?.task_id ||
          (resData?.status === 'pending' && resData?.id)

        if (taskId) {
          console.log(`Polling for style deletion (Task ID: ${taskId})...`)
          await deletePoller.startPolling(taskId)

          // After successful polling, remove from state
          if (isSystem) {
            setSystemStyles(prev => prev.filter(style => style.key !== styleId))
          } else {
            setCustomStyles(prev => prev.filter(style => style.key !== styleId))
          }
          message.success('Style deleted successfully')
          return { success: true }
        }

        throw new Error(resData?.message || 'Failed to delete style')
      } catch (error) {
        console.error('Error deleting style:', error)
        message.error(
          error.message || 'Failed to delete style. Please try again.'
        )
        return { success: false, error }
      } finally {
        setIsDeletingStyle(false)
        setDeletingStyleId(null)
      }
    },
    [deletePoller, videoId]
  )

  /**
   * Open the create style modal
   */
  const openCreateStyleModal = useCallback(() => {
    setIsModalVisible(true)
  }, [])

  /**
   * Close the create style modal
   */
  const closeCreateStyleModal = useCallback(() => {
    setIsModalVisible(false)
    setUploadProgress({})
  }, [])

  return {
    // State
    allStyles,
    systemStyles,
    customStyles,
    isLoadingStyles,
    isCreatingStyle,
    isDeletingStyle,
    deletingStyleId,
    uploadProgress,
    creationProgress: styleCreationPoller.progress, // Expose creation progress
    isModalVisible,
    currentAction,
    pollingStatus: {
      presigned: presignedUrlPoller.status,
      creation: styleCreationPoller.status
    },

    // Actions
    createCustomStyle,
    deleteStyle,
    openCreateStyleModal,
    closeCreateStyleModal,
    fetchPresignedUrls,
    uploadFileToPresignedUrl,
    refreshStyles: fetchStyles
  }
}

export default useShotGenerationStyles
